import React, { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Breadcrumb, Skeleton, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { CaretRightOutlined, HeartOutlined, HeartFilled, PlayCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import AppDownloadModal from '../../components/AppDownloadModal';
import httpClient from '../../utils/HttpClient';
import { COURSES_URL, COURSE_TEACHER_URL, TEACHER_URL, COURSE_FAVORITE_URL } from '../../constants/Url';
import { setOnlineClassDetailAction, setOnlineClassRelatedAction } from '../../modules/dataWebsite/actions';

const formatDuration = (seconds) => {
  if (!seconds) return '';
  if (typeof seconds === 'string' && seconds.includes(':')) {
    return seconds;
  }
  const secs = parseInt(seconds, 10);
  if (isNaN(secs)) return '';
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  const remainingSeconds = secs % 60;

  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(remainingSeconds).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${paddedMinutes}:${paddedSeconds}`;
  }
  return `${minutes}:${paddedSeconds}`;
};

export default function OnlineClassDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const appDownloadRef = useRef();

  const courseDetail = useSelector((state) => state.dataWebsiteReducer?.onlineClassDetailData);
  const relatedCourses = useSelector((state) => state.dataWebsiteReducer?.onlineClassRelatedData) || [];

  const [loading, setLoading] = useState(true);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState('');

  const handlePlayVideo = (videoUrlOrCourse) => {
    let videoUrl = "";
    if (typeof videoUrlOrCourse === 'string') {
      videoUrl = videoUrlOrCourse;
    } else if (videoUrlOrCourse) {
      videoUrl = videoUrlOrCourse.videoIntro  || videoUrlOrCourse?.videoName || "";
      if (!videoUrl && videoUrlOrCourse.videoIntro) {
        if (videoUrlOrCourse.picUrl && videoUrlOrCourse.picUrl.includes('/public/')) {
          const domainAndPublic = videoUrlOrCourse.picUrl.split('/public/')[0] + '/public/';
          const fileNameWithPrefix = videoUrlOrCourse.picUrl.substring(videoUrlOrCourse.picUrl.lastIndexOf('/') + 1);
          const originalVideoName = fileNameWithPrefix.split('_thumbnail.png')[0];
          videoUrl = domainAndPublic + originalVideoName;
        } else {
          videoUrl = videoUrlOrCourse.videoIntro;
        }
      }
    }
    setActiveVideoUrl(videoUrl);
    setIsVideoModalOpen(true);
  };

  const handleToggleFavorite = async (course, e) => {
    if (e) {
      e.stopPropagation();
    }
    try {
      const courseId = course.courseId || course._id || course.id;
      const isFav = course.isFavorited;
      const res = isFav
        ? await httpClient.delete(COURSE_FAVORITE_URL, { data: { courseId } })

            .then(res => res.data)
            .catch(error => ({ status: 500, message: error?.message }))
        : await httpClient.post(COURSE_FAVORITE_URL, { courseId })
            .then(res => res.data)
            .catch(error => ({ status: 500, message: error?.message }));

      if (res.status === 200) {
        message.success(t(res.message || "Success"));
        setOnlineClassRelatedAction(
          relatedCourses.map(c => {
            if ((c.courseId || c._id || c.id) === courseId) {
              return { ...c, isFavorited: !c.isFavorited };
            }
            return c;
          })
        );
      } else {
        message.error(t(res.message) || t("text_operation_failed"));
      }
    } catch (error) {
      console.error("text_toggle_error", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Pic 1: CALL API COURSES_URL
        const courseRes = await httpClient.get(`${TEACHER_URL}/${id}`)
          .then(res => res.data)
          .catch(error => ({ status: 500, message: error?.message }));

        if (courseRes.status === 200 && courseRes.data) {
          setOnlineClassDetailAction(courseRes.data);
          
          const teacherObj = courseRes.data.teacher || {};
          
          if (id) {
            const relatedRes = await httpClient.get(`${COURSE_TEACHER_URL}/${id}`)
              .then(res => res.data)
              .catch(error => ({ status: 500, message: error?.message }));
              
            if (relatedRes.status === 200 && relatedRes.data) {
              setOnlineClassRelatedAction(relatedRes.data || []);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching detail data", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const handleOpenDownloadApp = () => {
    appDownloadRef.current?.showModal();
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen pb-16 bg-[#fcfdfe]">
        <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="mb-6 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div 
              onClick={() => {
                if (window.history.state && window.history.state.idx > 0) {
                  navigate(-1);
                } else {
                  navigate('/online-class');
                }
              }} 
              className="inline-flex items-center gap-2 cursor-pointer text-gray-500 hover:text-black transition-colors font-medium text-sm"
            >
              <ArrowLeftOutlined className="text-base" />
              <span>{t('text_back_previous_page')}</span>
            </div>
            <Skeleton.Input active size="small" style={{ width: '250px' }} />
          </div>

          <div className="bg-white p-5 sm:p-8 md:p-10 flex flex-col md:flex-row gap-8 md:gap-12 items-center md:items-stretch mb-12">
            <div className="w-full max-w-[280px] sm:max-w-[320px] aspect-[3/4] rounded-lg overflow-hidden relative shadow-md shrink-0 bg-gray-50 flex items-center justify-center">
              <Skeleton.Image className="!w-full !h-full" active />
            </div>
            <div className="flex flex-col justify-between py-2 text-left w-full gap-4">
              <div className="flex flex-col gap-4">
                <Skeleton.Input active size="large" style={{ width: '200px' }} />
                <Skeleton.Input active size="small" style={{ width: '120px' }} />
                <Skeleton active paragraph={{ rows: 4 }} title={false} className="mt-4" />
              </div>
              <Skeleton.Button active size="large" style={{ width: '160px' }} />
            </div>
          </div>

          <section className="w-full">
            <div className="border-b border-gray-100 pb-4 mb-8">
              <Skeleton.Input active size="default" style={{ width: '120px' }} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white rounded-lg border border-gray-100 p-5 flex flex-col gap-4">
                  <div className="w-full aspect-[16/9] rounded-md bg-gray-50 flex items-center justify-center overflow-hidden">
                    <Skeleton.Image className="!w-full !h-full" active />
                  </div>
                  <Skeleton.Input active size="default" style={{ width: '80%' }} />
                  <Skeleton.Input active size="small" style={{ width: '40%' }} />
                  <div className="flex justify-between items-center pt-4 border-t border-gray-50 mt-auto">
                    <Skeleton.Input active size="small" style={{ width: '30%', height: '16px' }} />
                    <Skeleton.Input active size="small" style={{ width: '25%', height: '16px' }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen pb-16 bg-[#fcfdfe]">
      
      {/* ── Main Container ── */}
      <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        
        {/* Top Navigation Bar: Back Link & Breadcrumb */}
        <div className="mb-6 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div 
            onClick={() => {
              if (window.history.state && window.history.state.idx > 0) {
                navigate(-1);
              } else {
                navigate('/online-class');
              }
            }} 
            className="inline-flex items-center gap-2 cursor-pointer text-gray-500 hover:text-black transition-colors font-medium text-sm"
          >
            <ArrowLeftOutlined className="text-base" />
            <span>{t('text_back_previous_page')}</span>
          </div>

          <Breadcrumb separator=">">
            <Breadcrumb.Item onClick={() => navigate('/')} className="cursor-pointer hover:text-black">{t('text_homepage')}</Breadcrumb.Item>
            <Breadcrumb.Item onClick={() => navigate('/education')} className="cursor-pointer hover:text-black">{t('text_tech_edu')}</Breadcrumb.Item>
            <Breadcrumb.Item onClick={() => navigate('/online-class')} className="cursor-pointer hover:text-black">{t('menu_online_class')}</Breadcrumb.Item>
            <Breadcrumb.Item>{courseDetail.name} {courseDetail.enName}</Breadcrumb.Item>
          </Breadcrumb>
        </div>

        {/* Tutor Info Profile Card */}
        <div className="bg-white  p-5 sm:p-8 md:p-10 flex flex-col md:flex-row gap-8 md:gap-12 items-center md:items-stretch mb-12">
          {/* Profile Left Image */}
          <div className="w-full max-w-[280px] sm:max-w-[320px] aspect-[3/4] rounded-lg overflow-hidden relative shadow-md shrink-0">
            <img 
              src={courseDetail.picUrl} 
              alt={courseDetail.name} 
              className="w-full h-full object-cover" 
            />
          </div>

          {/* Profile Right Text Details */}
          <div className="flex flex-col justify-between py-2 text-left w-full">
            <div className="flex flex-col gap-4">
              {/* Names */}
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-950 m-0">
                  {courseDetail?.name}
                </h1>
                <div className="text-lg text-gray-400 font-semibold tracking-wide mt-1">
                  {courseDetail?.enName}
                </div>
              </div>

              {/* Bio Desc */}
              <p className="text-gray-500 text-sm sm:text-base leading-relaxed m-0 mt-2 whitespace-pre-line max-w-[680px]">
                {courseDetail.textIntro}
              </p>
            </div>

            {/* Video Intro Button */}
            <div className='p-[2px] sm:w-[240px] bg-gradient-to-r from-[#50c7f8] via-[#7887fd] to-[#df97fb] shadow-md'>
              <button onClick={() => handlePlayVideo(courseDetail)} className='flex items-center justify-center gap-2 px-5 py-2.5 text-white bg-gradient-to-r from-[#21163e] to-[#252554] hover:opacity-90 transition-all h-full w-full'>
                <PlayCircleOutlined className='text-2xl' />
                <span className='text-sm tracking-widest text-center'>{t('text_view_video_details')}</span>
              </button>
            </div>
          </div>

        </div>

        {/* ── Section: Related Courses (相关课程) ── */}
        <section className="w-full">
          <div className="pb-4 mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 m-0 tracking-wide">
              {t('text_related_course')}
            </h2>
            <p className="text-gray-400 text-xs mt-1.5 m-0">
              {t('text_related_course1')}
            </p>
          </div>

          {/* 3-Column Courses Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {relatedCourses.map((course) => (
              <div 
                key={course.courseId}
                className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full cursor-pointer"
                onClick={() => navigate(`/online-class-member/${course?.courseId}`, { state: { tutorId: id } })}
              >
                {/* Course Thumbnail */}
                <div 
                  className="w-full aspect-[16/9] overflow-hidden relative"
                >
                  <img 
                    src={course.imageUrl || course.coverImage || course.coverimage || course.picUrl || ''} 
                    alt={course.title || course.name} 
                    className="w-full h-full object-cover" 
                  />
                  {/* Play circle overlay */}
                  <div className="absolute inset-0 bg-black/25 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                    <PlayCircleOutlined className="text-white text-4xl" />
                  </div>
                  {/* Play duration tag */}
                  {course?.videoDuration && (
                    <div className="absolute bottom-2 right-2 bg-black/75 px-2 py-0.5 rounded text-[10px] text-white font-medium">
                      {formatDuration(course.videoDuration)}
                    </div>
                  )}
                </div>

                {/* Course Content details */}
                <div className="p-5 flex flex-col flex-1">
                  
                  {/* Tag and heart button */}
                  <div className="flex justify-between items-center mb-2.5">
                    {course.categoryId?.name ? (
                      <span className="text-[14px] font-semibold gradient-text-button py-0.5 rounded uppercase">
                        {t(course.categoryId?.name )}
                      </span>
                    ) : <span />}
                    {course.isFavorited ? (
                      <HeartFilled 
                        onClick={(e) => handleToggleFavorite(course, e)}
                        className="text-red-500 cursor-pointer transition-colors z-10 relative" 
                      />
                    ) : (
                      <HeartOutlined 
                        onClick={(e) => handleToggleFavorite(course, e)}
                        className="text-gray-400 hover:text-red-500 cursor-pointer transition-colors z-10 relative" 
                      />
                    )}
                  </div>

                  {/* Course Title */}
                  <h3 
                    className="text-[14px] sm:text-base font-bold text-gray-900 leading-snug transition-colors mb-4 line-clamp-1"
                  >
                    {course.title || ""}
                  </h3>

                  <p className="text-gray-500 text-xs sm:text-[13px] leading-relaxed line-clamp-3 mb-6 flex-1">
                      {course?.intro || ""}
                    </p>

                  {/* Price and selections details */}
                  <div className="flex justify-between items-center pt-3 border-t border-[#db7a94] mt-auto">
                    <span className="text-base sm:text-lg font-black text-gray-900">
                      &yen; {course.price || 0}
                    </span>
                    <span className="text-[11px] text-gray-400">
                     {t("text_member_can_buy")} {course?.validityPeriod || 0} {t('text_day_no')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <Modal
        open={isVideoModalOpen}
        onCancel={() => setIsVideoModalOpen(false)}
        footer={null}
        destroyOnClose
        width={800}
        closeIcon={
          <span style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            width: 32, 
            height: 32, 
            borderRadius: '50%', 
            backgroundColor: 'rgba(0, 0, 0, 0.7)', 
            color: '#ffffff', 
            fontSize: 16, 
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
            marginTop: -6,
            marginRight: -6
          }}>
            ✕
          </span>
        }
        styles={{ 
          body: { padding: 0, backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px', overflow: 'hidden' } 
        }}
        centered
      >
        <video 
          className="w-full aspect-video rounded-lg" 
          controls 
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          autoPlay 
          src={activeVideoUrl}
        >
          {t("text_tag_not_support")}
        </video>
      </Modal>

      <AppDownloadModal ref={appDownloadRef} />
    </div>
  );
}
